"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  EvidenceList,
  EvidenceSplit,
  ReferenceRow,
  styleLabel,
  VerdictCard,
} from "@/components/tools/reference-tools/style-verdict";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { splitReferences } from "@/lib/tool-kit/reference-tools/references";
import {
  combineVerdicts,
  identifyInText,
  identifyReference,
} from "@/lib/tool-kit/reference-tools/style-identifier";

/** English strings. A translated content file overrides these through `ui`. */
export const CITATION_STYLE_IDENTIFIER_STRINGS = {
  referencesLabel:
    "Your references — one per line, or separated by blank lines",
  referencesPlaceholder:
    "Kahneman, D., & Tversky, A. (1979). Prospect theory: An analysis of decision under risk. Econometrica, 47(2), 263–291. https://doi.org/10.2307/1914185\nKuhn, T. S. (1962). The structure of scientific revolutions. University of Chicago Press.",
  inTextLabel: "An in-text citation from the same paper (optional)",
  inTextPlaceholder: "(Kahneman & Tversky, 1979)",
  emptyPrompt:
    "Paste at least one reference. The verdict updates as you type, and nothing leaves your browser.",
  verdictHeading: "Most likely style",
  noPattern:
    "No style fingerprints found. Paste complete references — authors, year, title and source.",
  tooClose: "Too close to call between {styles}.",
  or: " or ",
  agreement:
    "one:{agreeing} of {count} reference points to it|other:{agreeing} of {count} references point to it",
  outliersNote:
    "one:{count} reference reads as a different style — see below.|other:{count} references read as a different style — see below.",
  checkerLink: "Check the list with the {name}",
  generatorLink: "Cite new sources in the {name}",
  compareLink: "Read the difference: {name}",
  splitHeading: "How the evidence splits",
  share: "{share} of the evidence",
  referencesHeading: "Reference by reference",
  outlier: "Differs from the list",
  clues: "one:{count} clue|other:{count} clues",
  noClues: "No style fingerprints in this reference.",
  inTextHeading: "In-text citation",
  inTextVerdict: "Reads as {style}.",
  italicsNote:
    "Plain text loses italics, one of the clearest differences between styles, so the verdict rests on punctuation, order and wording alone. Harvard here means Cite Them Right; other Harvard variants score as Harvard too.",
  styles: {
    apa: "APA 7",
    mla: "MLA 9",
    "chicago-nb": "Chicago notes-bibliography",
    "chicago-ad": "Chicago author-date",
    harvard: "Harvard (Cite Them Right)",
    ieee: "IEEE",
    vancouver: "Vancouver",
    ama: "AMA",
  },
  confidence: {
    high: "High confidence",
    medium: "Medium confidence",
    low: "Low confidence",
    none: "No verdict",
  },
  checkers: {
    apa: "APA Citation Checker",
    mla: "MLA Citation Checker",
    chicago: "Chicago Citation Checker",
    harvard: "Harvard Citation Checker",
  },
  generators: {
    apa: "APA Citation Generator",
    mla: "MLA Citation Generator",
    chicago: "Chicago Citation Generator",
    harvard: "Harvard Citation Generator",
    ieee: "IEEE Citation Generator",
    vancouver: "Vancouver Citation Generator",
    ama: "AMA Citation Generator",
  },
  comparisons: {
    "apa-harvard": "Harvard vs APA",
    "vancouver-ama": "AMA vs Vancouver",
    "mla-chicago": "MLA vs Chicago",
    "apa-chicago": "APA vs Chicago",
  },
  lookalikes: {
    "apa-harvard":
      "APA 7 and Cite Them Right Harvard look alike in plain text. APA puts a full stop after the bracketed year — (2005). — joins authors with & and spaces initials; Harvard writes (2005) with no full stop, joins authors with “and”, closes up initials (T.S.) and puts article titles in single quotation marks.",
    "vancouver-ama":
      "Vancouver and AMA both number the list and write names as Kuhn TS. AMA writes page ranges in full (16569-16572) and doi:10.… with no space; Vancouver shortens pages (16569-72), may add the month after the year and writes doi: 10.… with a space.",
    "mla-chicago":
      "MLA 9 and Chicago 18 write many references the same way — a book can be identical in both. Articles differ: MLA gives vol. 47, no. 2, 1979, pp. 263–91; Chicago gives 47, no. 2 (1979): 263–91.",
    "chicago-systems":
      "Chicago has two systems. Author-date puts the year straight after the authors; notes-bibliography puts it near the end.",
    "apa-chicago":
      "APA and Chicago author-date both put the year after the authors. APA brackets it — (2005). — and uses initials; Chicago author-date writes it bare — 2005. — with full first names.",
    "ieee-vancouver":
      "IEEE and Vancouver both number the list. IEEE puts initials before the surname (J. E. Hirsch) and article titles in quotation marks; Vancouver puts unpunctuated initials after it (Hirsch JE) and uses no quotation marks.",
  },
  evidence: {
    bracketNumber:
      "Starts with a bracketed number, [1] — how IEEE numbers its list.",
    dotNumber:
      "Starts with a number and a full stop, 1. — Vancouver and AMA lists, and Chicago notes.",
    noteForm:
      "Reads like a footnote: the author's name in normal order, then the publication details in parentheses — Chicago notes.",
    vancouverAuthors:
      "Surname followed by unpunctuated initials (Kuhn TS) — Vancouver and AMA.",
    invertedInitials: "Surname, then initials (Kuhn, T. S.) — APA and Harvard.",
    initialsNoSpace:
      "Initials closed up with no space (T.S.) — Cite Them Right Harvard; APA spaces them.",
    initialsFirst: "Initials before the surname (T. S. Kuhn) — IEEE.",
    invertedFullName:
      "First author inverted with a full first name (Kuhn, Thomas S.) — MLA and Chicago.",
    ampersand: "An ampersand (&) between authors — APA.",
    andConnector:
      "“and” between authors — Harvard, MLA, Chicago and IEEE; APA uses &.",
    ellipsisAuthors:
      "An ellipsis in the author list — APA 7's form for 21 or more authors.",
    etAl: "“et al.” in the reference itself — every style here shortens long author lists except APA 7, which lists up to 20 names.",
    yearParenPeriod:
      "Year in parentheses followed by a full stop, (2005). — APA.",
    apaDate:
      "A date written (2023, March 31). — APA's form for dated online content.",
    yearParenNoPeriod:
      "Year in parentheses with no full stop after it, (2005) Title — Harvard.",
    yearAfterAuthors:
      "The year alone straight after the authors, Kuhn, Thomas S. 1962. — Chicago author-date.",
    lateYear:
      "The year comes near the end, not after the authors — something APA, Harvard and Chicago author-date never do.",
    parenYearColon:
      "Year in parentheses followed by a colon and the pages, (2005): 16569–72 — Chicago notes-bibliography.",
    chicagoAdVolume:
      "Volume (issue): pages, 102 (46): 16569–72 — Chicago author-date.",
    commaNo: "Volume, no. issue, 102, no. 46 — Chicago.",
    volNo: "“vol.” and “no.” — MLA and IEEE.",
    mlaYear:
      "The date after the issue number, set off by commas, no. 46, 2005, — MLA.",
    pagesThenDate: "Pages before the date, pp. 263–291, Mar. 1979 — IEEE.",
    vancouverDate: "Year, semicolon, volume, 2005;102 — Vancouver and AMA.",
    vancouverMonth:
      "Year and abbreviated month before the volume, 2005 Nov;102 — Vancouver; AMA gives the year alone.",
    elidedPages:
      "Second page number shortened, 16569-72 — Vancouver; AMA writes it in full.",
    fullPages:
      "Page range written in full after the volume, 16569-16572 — AMA.",
    tightIssueComma:
      "Volume(issue) with no space, then a comma, 47(2), — APA and Harvard.",
    tightIssueColon:
      "Volume(issue): pages with no spaces, 47(2):263 — Vancouver and AMA.",
    dmyAbbreviated: "Day, abbreviated month, year, 31 Mar. 2023 — MLA.",
    mdyFull:
      "Month, day and year in full, March 31, 2023 — Chicago and AMA; MLA abbreviates the month.",
    doiHttps:
      "DOI written as a link, https://doi.org/… — APA 7 (MLA, Chicago and Harvard use the link form too).",
    doiNoSpace: "“doi:” with no space before the number — AMA.",
    doiSpace: "“doi:” followed by a space — Vancouver and IEEE.",
    linkTrailingPeriod:
      "A full stop after the closing DOI or URL — MLA, Chicago, Vancouver and IEEE.",
    linkNoTrailingPeriod:
      "No full stop after the closing DOI or URL — APA, Harvard and AMA.",
    commaBeforeLink:
      "A comma before the URL or DOI — MLA, which treats the link as the work's location.",
    periodBeforeLink:
      "A full stop before the URL — APA, Chicago and AMA; MLA uses a comma.",
    availableAt: "“Available at:” before the link — Harvard.",
    accessedHarvard: "(Accessed: 5 October 2026) in parentheses — Harvard.",
    availableFrom: "“Available from:” before the link — Vancouver.",
    citedBracket: "[cited 2026 Oct 5] — Vancouver.",
    internetBracket: "[Internet] after the title — Vancouver.",
    onlineWithoutInternet:
      "An online source without [Internet] — Vancouver would normally mark it.",
    accessedMdy:
      "“Accessed October 5, 2026” — AMA (Chicago writes it this way too).",
    accessedDmy: "“Accessed 5 Oct. 2026” — MLA.",
    retrievedFrom:
      "“Retrieved from” before a URL — an APA 6 habit; APA 7 dropped it.",
    retrievedDateFrom:
      "“Retrieved October 5, 2026, from” — APA 7, for pages that change over time.",
    onlineBracket: "[Online] — IEEE.",
    ieeeAvailable: "“Available:” before the URL — IEEE.",
    placePublisherYear:
      "Place: Publisher, Year at the end — Chicago (17th edition and earlier) and IEEE.",
    publisherSemicolonYear: "Publisher; Year at the end — Vancouver and AMA.",
    placePublisherSemicolon:
      "Place: Publisher; Year — Vancouver; AMA 11 leaves out the place.",
    mlaPress: "University-press abbreviations, U of Chicago P — MLA.",
    ieeeUniv: "“Univ.” abbreviated in the publisher's name — IEEE.",
    editionParen: "Edition in parentheses after the title, (2nd ed. — APA.",
    editionEdn: "“2nd edn” — Harvard.",
    editorsApa: "(Eds.) after the editors' names — APA.",
    editorsHarvard: "(ed.) or (eds) after the editors' names — Harvard.",
    editedBy: "“edited by” — MLA and Chicago.",
    editorsWord: "“editors.” spelled out — Vancouver.",
    editorsAma: "“eds.” after the editors' names — AMA.",
    inTitle: "“In” before the book's title — Chicago; MLA leaves it out.",
    inInitials: "“In” before the editors' initials, In L. A. Pervin — APA.",
    inLower: "Lower-case “in” after the chapter title — Harvard and IEEE.",
    inColon: "“In:” before the editors — Vancouver and AMA.",
    vancouverPagesEnd:
      "“p. 154–96.” at the end — where Vancouver puts a chapter's pages.",
    amaYearPages: "Year:pages, 1999:154-196 — AMA chapters.",
    titleCaseNumbered:
      "A book or report title in title case after Vancouver-style names — AMA; Vancouver uses sentence case.",
    sentenceCaseBook:
      "A book or report title in sentence case after Vancouver-style names — Vancouver; AMA capitalises book titles.",
    ppInParens: "“pp.” inside parentheses, (2nd ed., pp. 154–196) — APA.",
    pp: "“pp.” before the page numbers — MLA, Harvard and IEEE.",
    quotedTitlePeriod:
      "Title in double quotation marks, closed with a full stop inside — MLA and Chicago.",
    quotedTitleComma:
      "Title in double quotation marks, closed with a comma inside — IEEE, and Harvard with US punctuation.",
    singleQuotedTitle:
      "Title in single quotation marks — Cite Them Right Harvard.",
    itAuthorCommaYear: "Author, comma, year, (Smith, 2020) — APA and Harvard.",
    itAmpersand: "& between authors in the citation — APA.",
    itAnd: "“and” between authors, then a comma and the year — Harvard.",
    itAuthorYearNoComma:
      "Author and year with no comma, (Smith 2020) — Chicago author-date.",
    itAuthorPage: "Author and page with no year, (Smith 45) — MLA.",
    itNarrative:
      "Name followed by the year in parentheses, Smith (2020) — the author-date styles.",
    itBracketNumber: "A number in square brackets, [3] — IEEE.",
    itParenNumber: "A number in parentheses, (3) — Vancouver.",
    itSuperscript: "A superscript number — AMA (and Chicago notes).",
    itIbid: "“Ibid.” — Chicago notes.",
    itNote: "A numbered note — Chicago notes.",
  },
} as const;

export default function CitationStyleIdentifierWidget() {
  const strings = useToolUi(CITATION_STYLE_IDENTIFIER_STRINGS);
  const [text, setText] = useState("");
  const [inText, setInText] = useState("");

  const verdicts = useMemo(
    () => splitReferences(text).map(identifyReference),
    [text]
  );
  const inTextVerdict = useMemo(
    () => (inText.trim() ? identifyInText(inText) : null),
    [inText]
  );
  const list = useMemo(
    () => combineVerdicts(verdicts, inTextVerdict),
    [verdicts, inTextVerdict]
  );
  const outliers = new Set(list.outliers);
  const hasReferences = verdicts.length > 0;

  return (
    <section className="space-y-6">
      <TextAreaField
        id="csi-references"
        label={strings.referencesLabel}
        onChange={setText}
        placeholder={strings.referencesPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <TextField
        id="csi-in-text"
        label={strings.inTextLabel}
        onChange={setInText}
        placeholder={strings.inTextPlaceholder}
        value={inText}
      />
      <div aria-live="polite" className="space-y-6">
        {hasReferences ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {hasReferences && list.top === null ? (
          <Notice>{strings.noPattern}</Notice>
        ) : null}
        <VerdictCard count={verdicts.length} list={list} strings={strings} />
        {list.ranking.length > 0 ? (
          <EvidenceSplit list={list} strings={strings} />
        ) : null}
        {inTextVerdict?.top ? (
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.inTextHeading}</h3>
            <p className="text-foreground text-sm">
              {t(strings.inTextVerdict, {
                style: styleLabel(inTextVerdict, strings),
              })}
            </p>
            <EvidenceList strings={strings} verdict={inTextVerdict} />
          </div>
        ) : null}
        {hasReferences ? (
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.referencesHeading}</h3>
            <ol className="space-y-3">
              {verdicts.map((verdict, index) => (
                <ReferenceRow
                  // The pasted references in order: position is their identity.
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
                  key={index}
                  outlier={outliers.has(index)}
                  strings={strings}
                  verdict={verdict}
                />
              ))}
            </ol>
          </div>
        ) : null}
      </div>
      <p className={HINT}>{strings.italicsNote}</p>
    </section>
  );
}
